import {
  FavoriteHistoryUnavailableReason,
  FavoriteItem,
} from '../domain/favorites';

import { captureSentryException } from './captureSentryError';

const STORAGE_KEY_PREFIX = 'favorite_history_';
const MAX_HISTORY_COUNT = 5;

/**
 * 履歴更新を通知するカスタムイベント名
 */
const HISTORY_UPDATE_EVENT = 'favoriteHistoryUpdate';

/**
 * 履歴更新イベントを発火
 */
const dispatchHistoryUpdateEvent = (): void => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(HISTORY_UPDATE_EVENT));
  }
};

/**
 * ユーザーIDと画面名からローカルストレージのキーを取得
 */
const getStorageKey = (userId: string, pageName: string): string => {
  return `${STORAGE_KEY_PREFIX}${userId}_${pageName}`;
};

const isValidUnavailableReason = (
  value: unknown
): value is FavoriteHistoryUnavailableReason => {
  return value === 'explicitly_deleted' || value === 'missing_from_list';
};

/**
 * 履歴項目の型
 */
type HistoryItem = {
  favoriteId: number;
  name: string;
  pageName: string;
  timestamp: number;
  unavailableReason?: FavoriteHistoryUnavailableReason;
};

/**
 * 履歴項目の型検証
 */
const validateHistoryItem = (item: unknown): item is HistoryItem => {
  if (
    typeof item !== 'object' ||
    item === null ||
    typeof (item as HistoryItem).favoriteId !== 'number' ||
    typeof (item as HistoryItem).name !== 'string' ||
    typeof (item as HistoryItem).pageName !== 'string' ||
    typeof (item as HistoryItem).timestamp !== 'number'
  ) {
    return false;
  }
  const maybeReason = (item as HistoryItem).unavailableReason;
  if (maybeReason === undefined) {
    return true;
  }
  return isValidUnavailableReason(maybeReason);
};

const historyItemToFavoriteItem = (item: HistoryItem): FavoriteItem => ({
  id: item.favoriteId,
  name: item.name,
  pageName: item.pageName,
  ...(item.unavailableReason !== undefined
    ? { historyUnavailableReason: item.unavailableReason }
    : {}),
});

/**
 * 失効理由に応じた短い表示ラベル（履歴行の Chip 等）
 */
export const getFavoriteHistoryUnavailableLabel = (
  reason: FavoriteHistoryUnavailableReason
): string => {
  switch (reason) {
    case 'explicitly_deleted':
      return '削除済み';
    case 'missing_from_list':
      return '削除または公開OFF';
    default: {
      const exhaustive: never = reason;
      return exhaustive;
    }
  }
};

/**
 * スクリーンリーダー向けの説明（視覚ラベルより具体化）
 */
export const getFavoriteHistoryUnavailableDescription = (
  reason: FavoriteHistoryUnavailableReason,
  favoriteName: string | undefined
): string => {
  const namePart =
    favoriteName != null && favoriteName.length > 0
      ? `「${favoriteName}」は`
      : 'このお気に入りは';
  switch (reason) {
    case 'explicitly_deleted':
      return `${namePart}削除されたため適用できません`;
    case 'missing_from_list':
      return `${namePart}削除されたか、グローバル公開がOFFになったため一覧に表示されず、適用できません`;
    default: {
      const exhaustive: never = reason;
      return exhaustive;
    }
  }
};

/**
 * ローカルストレージから履歴を取得・解析
 */
const parseHistoryFromStorage = (historyJson: string): HistoryItem[] => {
  try {
    const parsed = JSON.parse(historyJson);
    if (!Array.isArray(parsed)) {
      console.warn('Invalid history data format, expected array');
      return [];
    }
    return parsed.filter(validateHistoryItem);
  } catch (error) {
    console.warn('Failed to parse history JSON:', error);
    return [];
  }
};

const getUserHistoryStorageKeyPrefix = (userId: string): string =>
  `${STORAGE_KEY_PREFIX}${userId}_`;

/**
 * 指定 userId の履歴用 localStorage キーかどうかを判定する。
 * `storage` イベントのフィルタリング等に利用する。
 */
export const isFavoriteHistoryStorageKey = (
  userId: string,
  key: string
): boolean => key.startsWith(getUserHistoryStorageKeyPrefix(userId));

const collectUserHistoryStorageKeys = (userId: string): string[] => {
  const keys: string[] = [];
  const userPrefix = getUserHistoryStorageKeyPrefix(userId);
  for (let index = 0; index < localStorage.length; index += 1) {
    const key = localStorage.key(index);
    if (key && key.startsWith(userPrefix)) {
      keys.push(key);
    }
  }
  return keys;
};

/**
 * お気に入り履歴を取得（各画面ごとに最大5個）
 */
export const getFavoriteHistory = (
  userId: string,
  pageName: string
): FavoriteItem[] => {
  if (typeof window === 'undefined' || !userId) {
    return [];
  }

  try {
    const storageKey = getStorageKey(userId, pageName);
    const historyJson = localStorage.getItem(storageKey);

    if (!historyJson) {
      return [];
    }

    const history = parseHistoryFromStorage(historyJson);

    const sortedHistory = [...history].sort(
      (a, b) => b.timestamp - a.timestamp
    );

    return sortedHistory.map(historyItemToFavoriteItem);
  } catch (error) {
    console.error('Failed to get favorite history:', error);
    return [];
  }
};

/**
 * お気に入りを履歴に追加
 */
export const addFavoriteToHistory = (
  userId: string,
  favorite: FavoriteItem
): void => {
  if (
    typeof window === 'undefined' ||
    !userId ||
    !favorite.id ||
    !favorite.pageName
  ) {
    return;
  }

  try {
    const storageKey = getStorageKey(userId, favorite.pageName);
    const historyJson = localStorage.getItem(storageKey);
    const previousHistory = historyJson
      ? parseHistoryFromStorage(historyJson)
      : [];

    const newHistoryItem: HistoryItem = {
      favoriteId: favorite.id,
      name: favorite.name || '',
      pageName: favorite.pageName,
      timestamp: Date.now(),
    };

    const nextHistory = [
      newHistoryItem,
      ...previousHistory.filter((item) => item.favoriteId !== favorite.id),
    ].slice(0, MAX_HISTORY_COUNT);

    localStorage.setItem(storageKey, JSON.stringify(nextHistory));
    dispatchHistoryUpdateEvent();
  } catch (error) {
    captureSentryException(error, 'add_favorite_to_history', {
      contexts: {
        favorite: {
          favoriteId: favorite.id,
          pageName: favorite.pageName,
        },
      },
    });
  }
};

/**
 * 履歴からお気に入りを削除
 */
export const removeFavoriteFromHistory = (
  userId: string,
  favoriteId: number,
  pageName: string
): void => {
  if (typeof window === 'undefined' || !userId) {
    return;
  }

  try {
    const storageKey = getStorageKey(userId, pageName);
    const historyJson = localStorage.getItem(storageKey);

    if (!historyJson) {
      return;
    }

    const history = parseHistoryFromStorage(historyJson);

    const filteredHistory = history.filter(
      (item) => item.favoriteId !== favoriteId
    );

    localStorage.setItem(storageKey, JSON.stringify(filteredHistory));
    dispatchHistoryUpdateEvent();
  } catch (error) {
    captureSentryException(error, 'remove_favorite_from_history', {
      contexts: {
        favorite: {
          favoriteId,
          pageName,
        },
      },
    });
  }
};

/**
 * 履歴を完全にクリア
 */
export const clearFavoriteHistory = (
  userId: string,
  pageName: string
): void => {
  if (typeof window === 'undefined' || !userId) {
    return;
  }

  try {
    const storageKey = getStorageKey(userId, pageName);
    localStorage.removeItem(storageKey);
    dispatchHistoryUpdateEvent();
  } catch (error) {
    captureSentryException(error, 'clear_favorite_history', {
      contexts: {
        favorite: {
          pageName,
        },
      },
    });
  }
};

/**
 * すべての画面の履歴を完全にクリア
 */
export const clearAllFavoriteHistory = (userId: string): void => {
  if (typeof window === 'undefined' || !userId) {
    return;
  }

  try {
    const keysToRemove = collectUserHistoryStorageKeys(userId);
    keysToRemove.forEach((key) => localStorage.removeItem(key));
    if (keysToRemove.length > 0) {
      dispatchHistoryUpdateEvent();
    }
  } catch (error) {
    captureSentryException(error, 'clear_all_favorite_history');
  }
};

/**
 * 「`history.map` が無変更時に同一参照を返す」前提で、
 * いずれかの要素が新しい参照に置き換わったかを検出する。
 */
const hasAnyItemReplaced = (
  next: HistoryItem[],
  prev: HistoryItem[]
): boolean => next.some((item, index) => item !== prev[index]);

type HistoryUpdate = {
  storageKey: string;
  nextHistory: HistoryItem[];
};

/**
 * 各ストレージキーについて、history を transform し、変化があるものだけを返す。
 * localStorage の読み取りはここで行い、書き込みは `applyHistoryUpdates` 側に集約する。
 */
const computeHistoryUpdates = (
  userId: string,
  transform: (history: HistoryItem[]) => HistoryItem[]
): HistoryUpdate[] =>
  collectUserHistoryStorageKeys(userId).flatMap((storageKey) => {
    const historyJson = localStorage.getItem(storageKey);
    if (!historyJson) {
      return [];
    }
    const history = parseHistoryFromStorage(historyJson);
    const nextHistory = transform(history);
    if (!hasAnyItemReplaced(nextHistory, history)) {
      return [];
    }
    return [{ storageKey, nextHistory }];
  });

/**
 * 計算結果を一括で localStorage に反映し、変更があれば 1 度だけ通知する。
 */
const applyHistoryUpdates = (updates: HistoryUpdate[]): void => {
  updates.forEach(({ storageKey, nextHistory }) => {
    localStorage.setItem(storageKey, JSON.stringify(nextHistory));
  });
  if (updates.length > 0) {
    dispatchHistoryUpdateEvent();
  }
};

const markFavoriteAsDeletedInHistory = (
  history: HistoryItem[],
  favoriteId: number
): HistoryItem[] =>
  history.map((item) => {
    if (item.favoriteId !== favoriteId) {
      return item;
    }
    if (item.unavailableReason === 'explicitly_deleted') {
      return item;
    }
    return { ...item, unavailableReason: 'explicitly_deleted' as const };
  });

const syncHistoryWithIdSet = (
  history: HistoryItem[],
  validIdSet: ReadonlySet<number>
): HistoryItem[] =>
  history.map((item) => {
    if (validIdSet.has(item.favoriteId)) {
      if (item.unavailableReason === undefined) {
        return item;
      }
      const { unavailableReason: _removed, ...cleared } = item;
      return cleared;
    }
    if (item.unavailableReason === 'explicitly_deleted') {
      return item;
    }
    if (item.unavailableReason === 'missing_from_list') {
      return item;
    }
    return { ...item, unavailableReason: 'missing_from_list' as const };
  });

/**
 * サーバー側削除成功後、履歴上の該当 ID を「明示的削除」としてマークする
 */
export const markHistoryFavoriteAsDeleted = (
  userId: string,
  favoriteId: number
): void => {
  if (typeof window === 'undefined' || !userId) {
    return;
  }

  try {
    const updates = computeHistoryUpdates(userId, (history) =>
      markFavoriteAsDeletedInHistory(history, favoriteId)
    );
    applyHistoryUpdates(updates);
  } catch (error) {
    captureSentryException(error, 'mark_history_favorite_as_deleted', {
      contexts: {
        favorite: {
          favoriteId,
        },
      },
    });
  }
};

/**
 * 一覧取得結果と履歴を突き合わせ、失効状態を同期する。
 * 既に explicitly_deleted のものは上書きしない（要件: API 再判定を待たない）。
 */
export const syncHistoryUnavailableStateWithFavoriteList = (
  userId: string,
  favorites: FavoriteItem[]
): void => {
  if (typeof window === 'undefined' || !userId) {
    return;
  }

  try {
    const validIdSet = new Set(
      favorites.flatMap((favorite) =>
        favorite.id != null ? [favorite.id] : []
      )
    );

    const updates = computeHistoryUpdates(userId, (history) =>
      syncHistoryWithIdSet(history, validIdSet)
    );
    applyHistoryUpdates(updates);
  } catch (error) {
    captureSentryException(
      error,
      'sync_history_unavailable_state_with_favorite_list'
    );
  }
};

/**
 * すべての画面の履歴を集約して取得（タイムスタンプ順、最新順）
 */
export const getAllFavoriteHistory = (userId: string): FavoriteItem[] => {
  if (typeof window === 'undefined' || !userId) {
    return [];
  }

  try {
    const allHistory: HistoryItem[] = collectUserHistoryStorageKeys(
      userId
    ).flatMap((key) => {
      const historyJson = localStorage.getItem(key);
      return historyJson ? parseHistoryFromStorage(historyJson) : [];
    });

    const sortedHistory = [...allHistory].sort(
      (a, b) => b.timestamp - a.timestamp
    );

    return sortedHistory.map(historyItemToFavoriteItem);
  } catch (error) {
    console.error('Failed to get all favorite history:', error);
    return [];
  }
};
